import axiosInstance from '../../../../lib/axios';
import type {
  AudiencePreviewRequest,
  AudiencePreviewResponse,
  CampaignDto,
  CampaignInput,
  CampaignListParams,
  CampaignListResponse,
  CampaignRecipientsParams,
  CampaignRecipientsResponse,
  CreateSuppressionInput,
  EmailPreviewResponse,
  ResolveEmailsResponse,
  SendCampaignInput,
  SuppressionListParams,
  SuppressionListResponse,
  SuppressionItem,
  TestNotificationResponse,
} from '../types';

const BASE_URL = '/v1/admin/notifications';

export const previewAudience = async (
  data: AudiencePreviewRequest
): Promise<AudiencePreviewResponse> => {
  const response = await axiosInstance.post<{ data: AudiencePreviewResponse }>(
    `${BASE_URL}/audience-preview`,
    data
  );
  return response.data.data;
};

export const resolveNotificationEmails = async (
  emails: string[]
): Promise<ResolveEmailsResponse> => {
  const response = await axiosInstance.post<{ data: ResolveEmailsResponse }>(
    `${BASE_URL}/audience/resolve-emails`,
    { emails }
  );
  return response.data.data;
};

export const getNotificationCampaigns = async (
  params: CampaignListParams
): Promise<CampaignListResponse> => {
  const response = await axiosInstance.get<{ data: CampaignListResponse }>(
    `${BASE_URL}/campaigns`,
    {
      params,
    }
  );
  return response.data.data;
};

export const createNotificationCampaign = async (data: CampaignInput): Promise<CampaignDto> => {
  const response = await axiosInstance.post<{ data: CampaignDto }>(`${BASE_URL}/campaigns`, data);
  return response.data.data;
};

export const getNotificationCampaign = async (publicId: string): Promise<CampaignDto> => {
  const response = await axiosInstance.get<{ data: CampaignDto }>(
    `${BASE_URL}/campaigns/${publicId}`
  );
  return response.data.data;
};

export const updateNotificationCampaign = async (
  publicId: string,
  data: Partial<CampaignInput>
): Promise<CampaignDto> => {
  const response = await axiosInstance.patch<{ data: CampaignDto }>(
    `${BASE_URL}/campaigns/${publicId}`,
    data
  );
  return response.data.data;
};

export const deleteNotificationCampaign = async (publicId: string): Promise<{ deleted: true }> => {
  const response = await axiosInstance.delete<{ data: { deleted: true } }>(
    `${BASE_URL}/campaigns/${publicId}`
  );
  return response.data.data;
};

export const getEmailPreview = async (
  publicId: string,
  locale?: 'vi' | 'en'
): Promise<EmailPreviewResponse> => {
  const response = await axiosInstance.post<{ data: EmailPreviewResponse }>(
    `${BASE_URL}/campaigns/${publicId}/email-preview`,
    { locale }
  );
  return response.data.data;
};

export const sendNotificationTest = async (
  publicId: string,
  emails?: string[]
): Promise<TestNotificationResponse> => {
  const response = await axiosInstance.post<{ data: TestNotificationResponse }>(
    `${BASE_URL}/campaigns/${publicId}/test`,
    emails && emails.length > 0 ? { emails } : {}
  );
  return response.data.data;
};

export const sendNotificationCampaign = async (
  publicId: string,
  data: SendCampaignInput
): Promise<CampaignDto> => {
  const response = await axiosInstance.post<{ data: CampaignDto }>(
    `${BASE_URL}/campaigns/${publicId}/send`,
    data
  );
  return response.data.data;
};

export const cancelNotificationCampaign = async (publicId: string): Promise<CampaignDto> => {
  const response = await axiosInstance.post<{ data: CampaignDto }>(
    `${BASE_URL}/campaigns/${publicId}/cancel`
  );
  return response.data.data;
};

export const retractNotificationCampaign = async (publicId: string): Promise<CampaignDto> => {
  const response = await axiosInstance.post<{ data: CampaignDto }>(
    `${BASE_URL}/campaigns/${publicId}/retract`
  );
  return response.data.data;
};

export const getNotificationRecipients = async (
  publicId: string,
  params: CampaignRecipientsParams
): Promise<CampaignRecipientsResponse> => {
  const response = await axiosInstance.get<{ data: CampaignRecipientsResponse }>(
    `${BASE_URL}/campaigns/${publicId}/recipients`,
    { params }
  );
  return response.data.data;
};

export const getSuppressions = async (
  params: SuppressionListParams
): Promise<SuppressionListResponse> => {
  const response = await axiosInstance.get<{ data: SuppressionListResponse }>(
    `${BASE_URL}/suppressions`,
    { params }
  );
  return response.data.data;
};

export const createSuppression = async (data: CreateSuppressionInput): Promise<SuppressionItem> => {
  const response = await axiosInstance.post<{ data: SuppressionItem }>(
    `${BASE_URL}/suppressions`,
    data
  );
  return response.data.data;
};

export const deleteSuppression = async (publicId: string): Promise<{ deleted: true }> => {
  const response = await axiosInstance.delete<{ data: { deleted: true } }>(
    `${BASE_URL}/suppressions/${publicId}`
  );
  return response.data.data;
};
